import React, { useState } from 'react';
import { X, Send, CheckCircle2, Loader2 } from 'lucide-react';
import { api } from '../services/api';

/**
 * Modal de inscrição em evento/ação de coletivo (armazenado em `programs`).
 * Endpoint: POST /api/programs/{program_id}/register
 * Payload: { nome, email, telefone?, mensagem? }
 */
export default function InscricaoEventoModal({ evento, onClose }) {
  const [form, setForm] = useState({ nome: '', email: '', telefone: '', mensagem: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  if (!evento) return null;

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.email.trim()) {
      setError('Nome e e-mail são obrigatórios.');
      return;
    }
    setLoading(true);
    try {
      await api.post(`/programs/${evento.evento_id}/register`, {
        nome: form.nome.trim(),
        email: form.email.trim().toLowerCase(),
        telefone: form.telefone.trim() || null,
        mensagem: form.mensagem.trim() || null,
      });
      setSuccess(true);
      setTimeout(() => onClose(), 2200);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Falha ao processar inscrição. Tente novamente.';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long' });
    } catch { return iso; }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="inscricao-modal-overlay"
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="relative bg-gradient-to-br from-[var(--primary)] to-purple-900 p-5 text-white">
          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 rounded-full p-1.5 hover:bg-white/20 transition"
            aria-label="Fechar"
            data-testid="inscricao-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
          <p className="text-xs uppercase tracking-wider opacity-80 mb-1">
            Inscrição — {evento.coletivo_nome}
          </p>
          <h2 className="text-lg font-bold leading-tight">{evento.titulo}</h2>
          <p className="text-sm opacity-90 mt-1">
            {formatDate(evento.data_inicio)}
            {evento.hora_inicio ? ` • ${evento.hora_inicio}` : ''}
            {evento.local ? ` • ${evento.local}` : ''}
          </p>
        </div>

        {/* Body */}
        {success ? (
          <div className="p-8 flex flex-col items-center gap-3 text-center" data-testid="inscricao-modal-success">
            <CheckCircle2 className="w-14 h-14 text-[var(--primary)]" />
            <h3 className="text-lg font-bold text-gray-900">Inscrição confirmada!</h3>
            <p className="text-sm text-gray-600 max-w-sm">
              A ONG receberá seus dados e entrará em contato pelos canais oficiais.
              Obrigado por participar.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Nome completo <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={form.nome}
                onChange={handleChange('nome')}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[var(--primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/20"
                placeholder="Como você quer ser chamado(a)"
                data-testid="inscricao-input-nome"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                E-mail <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                required
                value={form.email}
                onChange={handleChange('email')}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[var(--primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/20"
                placeholder="voce@exemplo.com"
                data-testid="inscricao-input-email"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Telefone / WhatsApp
              </label>
              <input
                type="tel"
                value={form.telefone}
                onChange={handleChange('telefone')}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[var(--primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/20"
                placeholder="(00) 00000-0000"
                data-testid="inscricao-input-telefone"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Mensagem (opcional)
              </label>
              <textarea
                value={form.mensagem}
                onChange={handleChange('mensagem')}
                rows={3}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[var(--primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/20 resize-none"
                placeholder="Alguma informação relevante para a ONG"
                data-testid="inscricao-input-mensagem"
              />
            </div>

            {error ? (
              <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-2" data-testid="inscricao-error">
                {error}
              </p>
            ) : null}

            <p className="text-xs text-gray-500 leading-relaxed">
              Ao enviar, você concorda que seus dados sejam compartilhados com a ONG organizadora
              exclusivamente para fins de contato sobre este evento.
            </p>

            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 transition"
                data-testid="inscricao-cancelar"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg bg-[var(--primary)] px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60 transition"
                data-testid="inscricao-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Confirmar inscrição
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
